import { z } from 'zod';
export var MeetingBodySeatAssignmentStatus;
(function (MeetingBodySeatAssignmentStatus) {
    MeetingBodySeatAssignmentStatus["Vacant"] = "vacant";
    MeetingBodySeatAssignmentStatus["Nominated"] = "nominated";
    MeetingBodySeatAssignmentStatus["Active"] = "active";
})(MeetingBodySeatAssignmentStatus || (MeetingBodySeatAssignmentStatus = {}));
const refineEndDate = (data) => {
    if (data.startDate && data.endDate) {
        return new Date(data.startDate).getTime() < new Date(data.endDate).getTime();
    }
    return true;
};
const refineEndDateMessage = {
    message: 'End date must be after start date',
    path: ['endDate'],
};
const refineCurrentTermAssignmentEndDate = (data) => {
    if (data.currentTermAssignmentEndDate) {
        return (new Date(data.currentTermAssignmentEndDate).getTime() <= new Date(data.startDate).getTime());
    }
    return true;
};
const refineCurrentTermAssignmentEndDateMessage = {
    message: 'Current term assignment end date must be before start date',
    path: ['currentTermAssignmentEndDate'],
};
// Create validation and DTO types for both server and client (needed for different date types)
const baseCreateSchema = z.object({
    startDate: z.iso.datetime('Start date is required'),
    endDate: z.iso.datetime().nullable().optional(),
    currentTermAssignmentEndDate: z.iso.datetime().nullable().optional(),
    endReason: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    meetingBodyPositionId: z.number('Meeting body position is required'),
    meetingMemberId: z
        .number('Meeting member is required')
        .describe('Required on create. Cannot be changed on update.'),
});
export const meetingBodySeatAssignmentCreateSchema = baseCreateSchema
    .refine(refineEndDate, refineEndDateMessage)
    .refine(refineCurrentTermAssignmentEndDate, refineCurrentTermAssignmentEndDateMessage);
export const meetingBodySeatAssignmentCreateClientSchema = baseCreateSchema
    .extend({
    startDate: z.date('Start date is required'),
    endDate: z.date().nullable().optional(),
    currentTermAssignmentEndDate: z.date().nullable().optional(),
})
    .refine(refineEndDate, refineEndDateMessage)
    .refine(refineCurrentTermAssignmentEndDate, refineCurrentTermAssignmentEndDateMessage);
/**
 * Schema for a previous position entry in position history.
 * Stores denormalized position title for display when positions are archived/renamed.
 * Only endDate is stored - startDate is computed from the previous entry's endDate
 * (or the assignment's startDate for the first entry).
 */
const previousPositionSchema = z.object({
    positionId: z.number(),
    positionTitle: z.string(),
    endDate: z.string(),
});
// Update validation and DTO type for both server and client (needed for different date types).
// meetingMemberId is intentionally not editable on update — changing who holds a seat must go
// through the vacate + create flow to preserve audit logs, emails, and term-number history.
export const meetingBodySeatAssignmentUpdateSchema = baseCreateSchema
    .omit({ currentTermAssignmentEndDate: true, meetingMemberId: true })
    .extend({
    previousPositions: z.array(previousPositionSchema).optional(),
})
    .partial()
    .refine(refineEndDate, refineEndDateMessage);
export const meetingBodySeatAssignmentUpdateClientSchema = baseCreateSchema
    .omit({ currentTermAssignmentEndDate: true, meetingMemberId: true })
    .extend({
    startDate: z.date('Start date is required'),
    endDate: z.date().nullable().optional(),
    previousPositions: z.array(previousPositionSchema).optional(),
})
    .partial()
    .refine(refineEndDate, refineEndDateMessage);
// Activate validation and DTO type for both server and client (needed for different date types)
export const meetingBodySeatAssignmentActivateSchema = z.object({
    startDate: z.iso.datetime().optional(),
    notes: z.string().nullish(),
});
export const meetingBodySeatAssignmentActivateClientSchema = meetingBodySeatAssignmentActivateSchema.extend({
    startDate: z.date().optional(),
});
// Vacate validation and DTO type for both server and client (needed for different date types)
export const meetingBodySeatAssignmentVacateSchema = z.object({
    endDate: z.iso.datetime('End date is required'),
    endReason: z.string('Seat assignment end reason is required'),
    notes: z.string().nullish(),
});
export const meetingBodySeatAssignmentVacateClientSchema = meetingBodySeatAssignmentVacateSchema.extend({
    endDate: z.date('End date is required'),
});
